<template>
  <Modal position="end" ref="modalRef" frameless-body @after-close="reset()">
    <template #header>
      <View direction="row" align="center" gap="sm">
        Conversation
        <IconButton
          v-if="conversationQuery.data.value?.messages?.length && isDevToolsEnabled"
          icon="copy"
          variant="ghost"
          size="small"
          @click="copyDialog"
          title="Copy conversation"
        />
      </View>
    </template>
    <template #body>
      <template v-if="conversationQuery.data.value">
        <View direction="row" gap="md" padding="md" align="center" class="property-info" v-if="activeInquiry">
          <PropertyPhotoPreview :image-url="activeInquiry.property.coverImage" size="xs" />
          <Text variant="label-medium" color="primary">
            {{ activeInquiry.property.displayName }}
          </Text>
        </View>

        <View padding="lg">
          <ConversationTranscript :conversation="conversationQuery.data.value" />
        </View>
      </template>

      <View v-else justify="center" padding="xl">
        <LoadingIndicator />
      </View>
    </template>

    <template #footer v-if="conversationQuery.data.value">
      <ConversationCoachBox :conversation-id="conversationQuery.data.value.id" />
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import { IconButton, LoadingIndicator, Modal, Text, View, useAlertsStore } from '@tallo/design-system';
import { ConversationCoachBox, ConversationTranscript } from '@tallo/investor/conversation';
import { useRenterConversationQuery, useRenterInquiriesQuery } from '@tallo/investor/renter';
import { PropertyPhotoPreview } from '@tallo/property';
import { isDevToolsEnabled as checkIsDevToolsEnabled } from '@tallo/utility';

const modalRef = ref<InstanceType<typeof Modal>>();

const conversationQuery = useRenterConversationQuery();
const inquiriesQuery = useRenterInquiriesQuery();
const alerts = useAlertsStore();

const activeInquiry = computed(() => {
  const inquiries = inquiriesQuery.data.value || [];
  return inquiries.find((inquiry) => inquiry.id === conversationQuery.activeInquiryId.value);
});

const isDevToolsEnabled = computed(() => checkIsDevToolsEnabled());

function show(inquiryId: string) {
  conversationQuery.setActiveInquiry(inquiryId);
  modalRef.value?.show();
}

function reset() {
  conversationQuery.setActiveInquiry(null);
}

async function copyDialog() {
  const messages = conversationQuery.data.value?.messages || [];

  if (!messages.length) {
    return;
  }

  const renterName = activeInquiry.value?.renter?.user?.name || 'Renter';

  const formattedDialog = messages
    .map((message) => {
      const speaker = message.isSystem ? 'Tallo' : renterName;
      return `- ${speaker}: ${message.content}`;
    })
    .join('\n');

  try {
    await navigator.clipboard.writeText(formattedDialog);
    alerts.success('Conversation copied to clipboard');
  } catch (error) {
    alerts.error('Failed to copy conversation');
  }
}

defineExpose({ modalRef, show });
</script>

<style scoped lang="scss">
.property-info {
  padding-left: var(--t-spacing-lg);
  border-bottom: 1px solid var(--t-color-border-secondary);
}
</style>
